'use client'

import type { Data } from '@repo/strapi'
import { useTranslations } from 'next-intl'
import CkEditorCSRRenderer from '@/components/elementary/CkEditorRenderer/CkEditorCSRRenderer'
import { Container } from '@/components/elementary/Container'
import { Tooltip } from '@/components/elementary/Tooltip'
import { ArrowDown } from '@/components/icons/ArrowDown'
import { Info } from '@/components/icons/Info'
import { BillingDetails } from '@/components/page-builder/global/pricingV1/featuredPlan/BillingDetails'
import { HighlightIcon } from '@/components/page-builder/global/pricingV1/overview/HighlightIcon'
import Heading from '@/components/typography/Heading'
import { Paragraph } from '@/components/typography/Paragraph'
import { FEATURE_COMPARISON_ANCHOR_ID } from '@/constants/pricing.constants'
import { cn } from '@/lib/styles'
import styles from '../featureComparison/FeatureCell.module.css'

export function FeaturedPlan({
	data,
}: {
	data?: Data.Component<'shared.featured-plan-v1'> | null
}) {
	const t = useTranslations('Pricing')

	if (!data) return null

	const { title, description, highlights, price, priceUnderline, button } = data
	return (
		<Container paddingX className="pb-12 lg:pb-10">
			<div className="p-2 grid place-items-center bg-peach-300 text-brand-black border-x border-t border-neutral-700 rounded-t-lg">
				<Paragraph fontSize="bodyBoldMedium" className="uppercase">
					{t('mostPopular')}
				</Paragraph>
			</div>

			<div className="grid lg:grid-cols-4 lg:gap-10 py-6.25 px-3.75 lg:p-10 border border-neutral-700 bg-brand-black">
				{/* Plan name & description */}
				<div className="flex flex-col gap-2.5 pb-6 lg:pb-0">
					<Heading tag="h2" className="w-fit text-neutral-light-100" fontSize="displaySmall">
						{title}
					</Heading>
					<Paragraph fontSize="headingSmallLight" className="text-neutral-light-100">
						{description}
					</Paragraph>
				</div>

				{/* Highlights */}
				<div className="flex flex-col gap-4.5 pb-4.5 lg:pb-0">
					{highlights?.slice(0, 5)?.map((highlight) => (
						<div key={highlight.id} className="flex items-start">
							<HighlightIcon icon={highlight.icon} />

							<CkEditorCSRRenderer
								htmlContent={highlight.text}
								className={cn(styles['table-cell-content'], 'text-neutral-light-100')}
								defaultTypography="bodyMedium"
							/>

							{highlight.tooltip ? (
								<Tooltip content={highlight.tooltip}>
									<span>
										<Info className="text-neutral-light-400 size-4 top-1 relative shrink-0 ml-1" />
									</span>
								</Tooltip>
							) : null}
						</div>
					))}
				</div>

				<div className="flex flex-col gap-4.5">
					{highlights?.slice(5)?.map((highlight) => (
						<div key={highlight.id} className="flex items-start">
							<HighlightIcon icon={highlight.icon} />

							<CkEditorCSRRenderer
								htmlContent={highlight.text}
								className={cn(styles['table-cell-content'], 'text-neutral-light-100')}
								defaultTypography="bodyMedium"
							/>

							{highlight.tooltip ? (
								<Tooltip content={highlight.tooltip}>
									<span>
										<Info className="text-neutral-light-400 size-4 top-1 relative shrink-0 ml-1" />
									</span>
								</Tooltip>
							) : null}
						</div>
					))}
				</div>

				{/* Billing details */}
				{price || priceUnderline || button?.link?.href ? (
					<BillingDetails
						title={title}
						price={price}
						priceUnderline={priceUnderline}
						button={button}
					/>
				) : null}
			</div>

			<a
				href={`#${FEATURE_COMPARISON_ANCHOR_ID}`}
				className="group p-2 flex items-center justify-center gap-2.75 bg-surface-featured text-neutral-light-100 border-x border-b border-neutral-700 rounded-b-lg hover:bg-surface-featured/90 transition-colors cursor-pointer"
			>
				<Paragraph fontSize="bodyBoldLarge" className="text-neutral-light-100">
					{t('comparePlans')}
				</Paragraph>
				<ArrowDown
					aria-hidden
					className="size-6 shrink-0 mt-0 group-hover:mt-1 transition-[margin] duration-200"
				/>
			</a>
		</Container>
	)
}
